import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core"
import {
    GenerateLyricsParameters,
    LyricsChatMessage,
    LyricsChatStreamEvent,
    MAX_LYRICS_CHAT_MESSAGES,
    VocalGender,
} from "@common-lib/interfaces/api/content.api"
import { GeneralService } from "@services/general/general.service"
import { TracksHttpService } from "@services/tracks-http.service"

type LyricsChatMessageStatus = "complete" | "streaming" | "error"

interface LyricsChatDisplayMessage extends LyricsChatMessage {
    status: LyricsChatMessageStatus
}

@Component({
    selector: "lyrics-chat",
    templateUrl: "lyrics-chat.component.html",
    styleUrls: ["lyrics-chat.component.scss"],
})
export class LyricsChatComponent implements AfterViewChecked, OnChanges, OnDestroy {
    @Input() open: boolean = false
    @Input() musicDescription: string = ""
    @Input() currentLyrics: string = ""
    @Input() vocalGender: VocalGender | "" = ""
    @Output() close = new EventEmitter<void>()
    @Output() useLyrics = new EventEmitter<string>()

    @ViewChild("messagesContainer")
    private messagesContainer?: ElementRef<HTMLElement>

    public messages: LyricsChatDisplayMessage[] = []
    public composerValue: string = ""
    public generating: boolean = false
    public errorMessage: string = ""
    public lyricsQuotaLeft: number | null = null
    public lyricsQuotaTotal: number | null = null
    public lyricsQuotaPeriod: "day" | "month" = "day"
    private controller?: AbortController
    private shouldScrollToBottom: boolean = false
    private readonly wordStreamDelay: number = 24
    private wordStreamQueue: string[] = []
    private pendingWordFragment: string = ""
    private wordStreamTimer?: ReturnType<typeof setTimeout>
    private wordStreamDrainResolver?: () => void
    private wordStreamMessage?: LyricsChatDisplayMessage
    private streamDoneReceived: boolean = false

    constructor(
        private tracksHttp: TracksHttpService,
        private generalService: GeneralService,
        private ref: ChangeDetectorRef
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.open?.currentValue === true) {
            void this.refreshLyricsQuota()
        }
    }

    public get lyricsQuotaReached(): boolean {
        return this.lyricsQuotaLeft === 0
    }

    public getLyricsQuotaLabel(): string | null {
        if (this.lyricsQuotaLeft === null) {
            return null
        }

        if (this.lyricsQuotaPeriod === "month") {
            return `${this.lyricsQuotaLeft} lyrics left this month`
        }

        return `${this.lyricsQuotaLeft} lyrics generation left today in your quota`
    }

    private async refreshLyricsQuota(): Promise<void> {
        await this.generalService.getLyricsGenerationQuota()
        const quota = this.generalService.lyricsGenerationQuota.getValue()

        if (quota === null) {
            return
        }

        const monthlyIsTighter =
            quota.monthlyGenerationsLeft < quota.dailyGenerationsLeft

        this.lyricsQuotaLeft = monthlyIsTighter
            ? quota.monthlyGenerationsLeft
            : quota.dailyGenerationsLeft
        this.lyricsQuotaTotal = monthlyIsTighter
            ? quota.monthlyGenerationsQuota
            : quota.dailyGenerationsQuota
        this.lyricsQuotaPeriod = monthlyIsTighter ? "month" : "day"
        this.ref.markForCheck()
    }

    public ngAfterViewChecked(): void {
        if (!this.shouldScrollToBottom || !this.messagesContainer) {
            return
        }

        const element = this.messagesContainer.nativeElement
        element.scrollTop = element.scrollHeight
        this.shouldScrollToBottom = false
    }

    public ngOnDestroy(): void {
        this.controller?.abort()
        this.resetWordStream()
    }

    public closeDrawer(): void {
        this.close.emit()
    }

    public onComposerKeydown(event: KeyboardEvent): void {
        if (event.key !== "Enter" || event.shiftKey || event.isComposing) {
            return
        }

        event.preventDefault()
        void this.sendMessage()
    }

    public async sendMessage(): Promise<void> {
        const content = this.composerValue.trim()

        if (!content || this.generating || this.lyricsQuotaReached) {
            return
        }

        this.messages.push({
            role: "user",
            content,
            status: "complete",
        })
        this.composerValue = ""
        await this.requestLyrics()
    }

    public async retry(): Promise<void> {
        if (this.generating || this.lyricsQuotaReached) {
            return
        }

        const failedMessageIndex = this.messages.findIndex(
            message => message.status === "error"
        )

        if (failedMessageIndex !== -1) {
            this.messages.splice(failedMessageIndex, 1)
        }

        await this.requestLyrics()
    }

    public applyLyrics(content: string): void {
        this.useLyrics.emit(content)
    }

    private async requestLyrics(): Promise<void> {
        const history = this.messages
            .filter(message => message.status === "complete")
            .slice(-MAX_LYRICS_CHAT_MESSAGES)
            .map(({ role, content }) => ({ role, content }))

        if (
            history.length === 0 ||
            history[history.length - 1].role !== "user"
        ) {
            return
        }

        const assistantMessage: LyricsChatDisplayMessage = {
            role: "assistant",
            content: "",
            status: "streaming",
        }
        this.messages.push(assistantMessage)
        this.resetWordStream()
        this.wordStreamMessage = assistantMessage
        this.generating = true
        this.errorMessage = ""
        this.controller = new AbortController()
        this.shouldScrollToBottom = true

        const parameters: GenerateLyricsParameters = {
            messages: history,
            musicDescription: this.musicDescription,
            currentLyrics: this.currentLyrics,
            ...(this.vocalGender ? { vocalGender: this.vocalGender } : {}),
        }

        try {
            await this.tracksHttp.generateLyrics(
                parameters,
                this.controller,
                event => this.handleStreamEvent(event)
            )

            if (!this.streamDoneReceived) {
                throw new Error("The lyrics stream ended before completion.")
            }

            await this.waitForWordStreamToDrain()

            if (!assistantMessage.content.trim()) {
                throw new Error(
                    "The lyrics assistant returned an empty response."
                )
            }

            assistantMessage.status = "complete"
            await this.refreshLyricsQuota()
            this.ref.markForCheck()
        } catch (error) {
            if (this.controller.signal.aborted) {
                return
            }

            this.resetWordStream()
            assistantMessage.status = "error"
            this.errorMessage =
                error instanceof Error && error.message
                    ? error.message
                    : "We couldn't generate lyrics. Please try again."
        } finally {
            this.generating = false
            this.controller = undefined
            this.shouldScrollToBottom = true
        }
    }

    private handleStreamEvent(event: LyricsChatStreamEvent): void {
        if (event.type === "delta") {
            this.queueLyricsDelta(event.content)
        } else if (event.type === "done") {
            this.streamDoneReceived = true
            this.flushPendingWordFragment()
        } else {
            throw new Error(event.message)
        }

        this.shouldScrollToBottom = true
    }

    private queueLyricsDelta(content: string): void {
        this.pendingWordFragment += content

        const completeWordPattern = /\s*\S+\s+/g
        let consumedLength = 0
        let match: RegExpExecArray | null

        while (
            (match = completeWordPattern.exec(this.pendingWordFragment)) !==
            null
        ) {
            this.wordStreamQueue.push(match[0])
            consumedLength = completeWordPattern.lastIndex
        }

        this.pendingWordFragment =
            this.pendingWordFragment.slice(consumedLength)
        this.scheduleNextWord()
    }

    private flushPendingWordFragment(): void {
        if (this.pendingWordFragment) {
            this.wordStreamQueue.push(this.pendingWordFragment)
            this.pendingWordFragment = ""
        }

        this.scheduleNextWord()
    }

    private scheduleNextWord(): void {
        if (this.wordStreamTimer != null || this.wordStreamQueue.length === 0) {
            this.resolveWordStreamDrainIfReady()
            return
        }

        this.wordStreamTimer = setTimeout(() => {
            this.wordStreamTimer = undefined

            const nextWord = this.wordStreamQueue.shift()
            if (
                nextWord != null &&
                this.wordStreamMessage?.status === "streaming"
            ) {
                this.wordStreamMessage.content += nextWord
                this.shouldScrollToBottom = true
                this.ref.markForCheck()
            }

            this.scheduleNextWord()
        }, this.wordStreamDelay)
    }

    private waitForWordStreamToDrain(): Promise<void> {
        if (this.wordStreamQueue.length === 0 && this.wordStreamTimer == null) {
            return Promise.resolve()
        }

        return new Promise(resolve => {
            this.wordStreamDrainResolver = resolve
        })
    }

    private resolveWordStreamDrainIfReady(): void {
        if (
            this.wordStreamQueue.length !== 0 ||
            this.wordStreamTimer != null ||
            this.wordStreamDrainResolver == null
        ) {
            return
        }

        const resolve = this.wordStreamDrainResolver
        this.wordStreamDrainResolver = undefined
        resolve()
    }

    private resetWordStream(): void {
        if (this.wordStreamTimer != null) {
            clearTimeout(this.wordStreamTimer)
        }

        this.wordStreamTimer = undefined
        this.wordStreamQueue = []
        this.pendingWordFragment = ""
        this.wordStreamMessage = undefined
        this.streamDoneReceived = false

        const resolve = this.wordStreamDrainResolver
        this.wordStreamDrainResolver = undefined
        resolve?.()
    }
}
